/**
 * Passkey (WebAuthn) sign-in state machine for the web login page.
 *
 * Deliberately shaped like `use-password-sign-in.ts` — same `onSignedIn`
 * contract, same `isLoading` / `showMFAInput` / `userEmail` / `verifyMFA`
 * surface — so the page can select ONE step-up controller by source
 * (`MfaStepUpController`) instead of wiring two half-overlapping ones.
 *
 * Four behaviours go beyond the password path, each measured rather than
 * defensive (see `docs/plans/2026-07-25-feat-passkey-webauthn-authentication-plan.md`
 * → "Spike findings (U3, 2026-07-26)"):
 *
 *  1. **Fails closed when the step-up check cannot answer.** A passkey session is
 *     aal1 even for a TOTP-enrolled user, so the check is the only thing between
 *     the ceremony and the app. A check that times out is a check that cannot
 *     answer, and lands on the same fail-closed branch.
 *  2. **Dismissing the step-up signs out, awaited** — `mfa-verify.tsx`'s cancel
 *     semantics, not `dismissMFA`'s (which leaves the aal1 session live).
 *  3. **Every exit from an in-flight ceremony is defined:**
 *     - Back → the revoke is awaited, then the form returns.
 *     - unmount/route change BEFORE the ceremony resolves → aborted through the
 *       adapter's signal; no session is ever minted.
 *     - any exit AFTER it resolves (including `aborted: true`, where the abort
 *       raced the browser and the session is real) → a fire-and-forget revoke,
 *       because there is no mounted component left to await in.
 *     - unmount AFTER it resolves with a step-up still owed → the same
 *       compensating revoke, gated on the obligation rather than fired
 *       blindly: this page also unmounts on SUCCESS (verify → `onSignedIn` →
 *       navigate), where the session is a complete aal2 one.
 *     - reload or tab close in that same window → nothing runs and the aal1
 *       session survives; the route guard's step-up check challenges it on the
 *       next navigation. Recorded as a residual, not solved here.
 *  4. **Nothing is torn down that this flow cannot prove it minted.** The
 *     ceremony cap hands the page back to the user on purpose, so a continuation
 *     can outlive the attempt that started it and land while the user is signed
 *     in by some OTHER method entirely. Two discriminators separate "clean up
 *     after myself" from "revoke whatever the user is signed in as now":
 *     - a **session identity** (`MintedSession`) for anything that TEARS DOWN —
 *       compensate only while the stored session is still the one this ceremony
 *       minted. See `passkey-session-teardown.ts` for why that question, and not
 *       "did another passkey attempt start", is the one that matters.
 *     - a **mount epoch** (`use-passkey-attempt-lifecycle.ts`) for anything that
 *       WRITES STATE or signs a user in — a plain "is this instance torn down"
 *       flag flips back to false the moment a preserved-state remount brings the
 *       instance back, and a continuation from before that teardown must not be
 *       treated as live.
 *
 * Every await this hook OWNS is bounded (`passkey-deadlines.ts`) - the ceremony
 * and the step-up check both, because a cap on the ceremony alone would only
 * move the latch into the check behind it.
 *
 * `onSignedIn` is the exception, and it is the host's await rather than this
 * hook's: a caller whose callback never settles still latches `isLoading`. It is
 * left unbounded deliberately - the callback completes a sign-in that has
 * already succeeded, and a hook-side cap would abandon it mid-redirect with the
 * session live. The bound belongs at the call site.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MFA_VERIFY_ERROR_KEYS, classifyMfaVerifyError } from '@/components/auth/mfa-verify-error';
import {
  CEREMONY_TIMED_OUT,
  CEREMONY_TIMEOUT_MS,
  startCeremonyDeadline,
  withRequestDeadline,
} from '@/components/auth/passkey-deadlines';
import {
  captureMintedSession,
  createPasskeySessionTeardown,
  stillHoldsMintedSession,
} from '@/components/auth/passkey-session-teardown';
import { usePasskeyAttemptLifecycle } from '@/components/auth/use-passkey-attempt-lifecycle';
import { useToast } from '@/hooks/use-toast';
import { checkMFARequirementStrict, verifyMFACode } from '@/lib/auth-utils';
import { clearPasskeyHint, setPasskeyHint } from '@/lib/passkey-hint';
import { mapPasskeyError, signInWithPasskey as runPasskeyCeremony } from '@/lib/passkeys';

import type { MintedSession } from '@/components/auth/passkey-session-teardown';
import type { MFARequirement } from '@/lib/auth-utils';
import type { PasskeySignInResult } from '@/lib/passkeys';

/**
 * What the page should say inline (and announce) after a ceremony settles.
 * Every one of these is a non-toast state: a cancel is a non-event, and the
 * password field is always right there as the alternative.
 */
export type PasskeySignInNotice = 'none' | 'cancelled' | 'not_found' | 'disabled' | 'check_failed';

/**
 * The slice of a sign-in hook the shared `MFALoginView` needs. Both this hook
 * and `usePasswordSignIn` satisfy it, which is what lets the login page hold a
 * single step-up controller keyed on which flow started it.
 */
export interface MfaStepUpController {
  readonly isLoading: boolean;
  readonly showMFAInput: boolean;
  readonly userEmail: string;
  readonly verifyMFA: (code: string) => Promise<void>;
  /**
   * Call-and-forget from the view's perspective. BOTH implementations are
   * asynchronous inside - each awaits a revoke before letting the form back -
   * and the interface narrows to `void` deliberately, so no call site can start
   * awaiting a teardown the UI must never block on.
   */
  readonly dismissMFA: () => void;
  /**
   * Abandon whatever this flow still has live, for a host that DISMISSES its
   * sign-in UI without unmounting - a sheet closing, a panel collapsing - where
   * the unmount cleanup that normally covers this never runs. Aborts an
   * in-flight ceremony so an OS prompt cannot outlive the UI and mint a session
   * into it, then discharges the step-up obligation if one is armed.
   *
   * Gated on the OBLIGATION, never on `showMFAInput`: the flag is armed the
   * moment a session is persisted, which covers the credential POST, the AAL
   * lookup, and a strict check that could not answer - three live-session
   * windows that mount no view at all. Gating on the view instead would both
   * miss those and fire a spurious sign-out during the pre-session POST.
   *
   * The obligation is re-read from the hook's own current state, never from a
   * value the caller captured: the call site that matters most runs in an async
   * continuation, where a captured boolean is a render old and reads false for
   * exactly the session that needs tearing down.
   */
  readonly abandonSignIn: () => void;
}

export interface UsePasskeySignInOptions {
  /** Called once the passkey (and MFA step-up, if owed) flow completes. */
  onSignedIn: (userId: string) => void | Promise<void>;
  /** Optional context-tagged error log. Defaults to no-op. */
  logError?: (label: string, error: unknown) => void;
}

export interface UsePasskeySignInResult extends MfaStepUpController {
  readonly notice: PasskeySignInNotice;
  /**
   * Named for the ceremony rather than mirroring `signInWithPassword`: the seam
   * guard (`test/guards/passkey-seam.guard.test.ts`) flags a dot-prefixed `signInWithPasskey`
   * call anywhere outside the adapter, so a same-named hook method makes every
   * call site look like a raw SDK call. The guard scans raw source text —
   * comments included — so do not spell the dotted form out here either.
   */
  readonly startPasskeySignIn: () => Promise<void>;
  /** Re-runs a step-up check that could not answer. Needs no new ceremony. */
  readonly retryStepUpCheck: () => Promise<void>;
  /**
   * Drops the retry affordance and the user id behind it, for when another flow
   * has taken over the page's step-up controller. Both halves matter: the notice
   * is what renders the Retry button, and the id is what a retry would hand
   * `onSignedIn` — against whatever session is current by then, which may belong
   * to a different user.
   *
   * Does NOT sign out. The claiming flow's own sign-in call races this one, and
   * a revoke issued into that race would be aimed at a storage slot changing
   * hands mid-call. The abandoned aal1 session is the residual the header
   * records: the route guard's step-up check challenges it.
   */
  readonly releaseStepUp: () => void;
  /**
   * Drops the advisory notice and nothing else, for a host reopening a
   * dismissed sign-in surface. Separate from `abandonSignIn` on purpose: that
   * one owns session teardown, and wanting a clean slate visually is not a
   * request for anything to happen to a session.
   */
  readonly clearNotice: () => void;
}

const SIGN_IN_FAILED_TITLE = 'passkey.signInFailedTitle';
const ABANDON_LABEL = 'passkeyAbandonSignOut';

/** A check that ran out of time has not answered, which is the closed branch. */
const CHECK_TIMED_OUT: MFARequirement = { status: 'indeterminate' };

/**
 * A verify that ran out of time. `timeout` is one of the classifier's system
 * fragments, so it surfaces as "our problem" rather than as a rejected code.
 */
const VERIFY_TIMED_OUT = { success: false, error: 'timeout' } as const;

export function usePasskeySignIn(options: UsePasskeySignInOptions): UsePasskeySignInResult {
  const [isLoading, setIsLoading] = useState(false);
  const [showMFAInput, setShowMFAInput] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState('');
  const [signedInUserId, setSignedInUserId] = useState<string | null>(null);
  const [notice, setNotice] = useState<PasskeySignInNotice>('none');
  const { toast } = useToast();
  const { t } = useTranslation('auth');

  /**
   * Non-null exactly while this flow holds a session it may still have to undo.
   * Cleared the moment the session becomes one the user is entitled to keep.
   */
  const mintedSessionRef = useRef<MintedSession | null>(null);
  /**
   * True only while an owed step-up sits idle — the ceremony minted a real aal1
   * session, the UI that would discharge it is on screen, and nothing is in
   * flight. Every async continuation that can settle the obligation takes it
   * over by clearing this on entry (`resolveStepUp` re-arms it when the check
   * lands back on an owed state), so the unmount compensation below and a live
   * continuation can never sign the same session out twice.
   */
  const stepUpOwedRef = useRef(false);

  // Identity-guarded and session-scoped. See `passkey-session-teardown.ts` for
  // why neither is a one-liner at its call site.
  const { revokeMintedSession } = createPasskeySessionTeardown(options.logError);

  const { ceremonyRef, deadlineRef, currentEpoch, isCurrent } = usePasskeyAttemptLifecycle({
    // A remount that PRESERVES state (`<Activity>`, StrictMode) comes back with
    // whatever `isLoading` the last teardown froze. That teardown aborted and
    // forgot the ceremony behind it, so nothing is left to release it and the
    // button would spin on a page with no ceremony at all. No-op when already false.
    onRemount: () => {
      setIsLoading(false);
    },
    // Back (`dismissMFA`) is only ONE way out of an owed step-up: a browser
    // Back, a link, or a programmatic redirect all land here instead, and the
    // ceremony abort signs nothing out because the ceremony already finished.
    onTeardown: () => {
      if (!stepUpOwedRef.current) {
        return;
      }
      const minted = mintedSessionRef.current;
      // The obligation is discharged by this call whatever its outcome — a
      // second attempt would only race it, and nothing is mounted to retry in.
      stepUpOwedRef.current = false;
      mintedSessionRef.current = null;
      void revokeMintedSession(ABANDON_LABEL, minted);
    },
  });

  /**
   * Takes the session it is resolving for rather than reading the ref: on a
   * preserved-state remount the ref may already belong to a newer attempt, and a
   * stale continuation must never be able to reach into it.
   */
  const resolveStepUp = async (
    userId: string,
    minted: MintedSession | null,
    epoch: number,
  ): Promise<void> => {
    const requirement = await withRequestDeadline(checkMFARequirementStrict(), CHECK_TIMED_OUT);
    if (!isCurrent(epoch)) {
      // The ceremony already resolved, so this is a real aal1 session with no
      // step-up UI left to mount — the header's "any exit AFTER it resolves"
      // clause, which an early return alone would break.
      void revokeMintedSession(ABANDON_LABEL, minted);
      return;
    }

    if (requirement.status === 'indeterminate') {
      // FAIL CLOSED here. The lossy `checkMFARequirement()` answers
      // `needsMFA: false` on every error path, so proceeding would let a
      // TOTP-enrolled user into the app un-stepped-up on a transient AAL lookup
      // failure. The session is kept so the retry re-reads the level without a
      // second biometric gesture.
      //
      // The session is NOT contained meanwhile: the route guard reads the lossy
      // variant, so the same lookup failure resolves there as "no step-up owed"
      // and admits the user on any direct navigation. This block is the only
      // strict gate on the passkey path; ADR 0068 records that web AAL
      // enforcement is client-side and lists the residual.
      stepUpOwedRef.current = true;
      setNotice('check_failed');
      return;
    }

    if (requirement.status === 'required') {
      stepUpOwedRef.current = true;
      setMfaFactorId(requirement.factorId);
      setShowMFAInput(true);
      return;
    }

    // Nothing owed, so the unmount that `onSignedIn`'s navigation causes must
    // leave this session alone. Set rather than assumed: a ceremony re-run after
    // a `check_failed` arrives here with the obligation still armed.
    // IDENTITY, not just the mount. `isCurrent(epoch)` proves this continuation
    // belongs to the mount that started it - which a retry running in the SAME
    // mount satisfies even after a password or OAuth sign-in has replaced the
    // stored session underneath it. `userId` is the one the CEREMONY minted, so
    // admitting it against somebody else's session signs the app in as the wrong
    // person. On a shared office machine that is not a hypothetical.
    //
    // `minted === null` proceeds: that is the pre-existing "this flow never
    // established what it was holding" case, and refusing there would block a
    // legitimate sign-in over a storage read that failed at ceremony time.
    if (minted !== null && !(await stillHoldsMintedSession(minted))) {
      options.logError?.('passkeyStepUpSessionChanged', { name: 'SessionReplaced' });
      return;
    }

    stepUpOwedRef.current = false;
    mintedSessionRef.current = null;
    await options.onSignedIn(userId);
  };

  const reportCeremonyFailure = (error: unknown): void => {
    // Classified FIRST: `isLockContentionError` and `isTransientFetchError` both
    // match a bare `AbortError`, which is exactly what a cancelled or superseded
    // ceremony produces, so consulting either before this reports a user cancel
    // as a network blip.
    const mapped = mapPasskeyError(error);

    if (mapped.code === 'cancelled') {
      // An empty account chooser, a dismissed sheet, a timeout: non-events.
      setNotice('cancelled');
      return;
    }

    if (mapped.code === 'credential_not_found' || mapped.code === 'disabled') {
      // Server truth beat the hint. Stop promoting the affordance on a browser
      // whose credentials are gone, or in a project with passkeys switched off.
      clearPasskeyHint();
      setNotice(mapped.code === 'disabled' ? 'disabled' : 'not_found');
      return;
    }

    if (mapped.code === 'user_banned') {
      toast({
        title: t(SIGN_IN_FAILED_TITLE),
        description: t('passkey.bannedDescription'),
        variant: 'destructive',
      });
      return;
    }

    // Only the unrecognised tail is logged — everything above is an expected
    // outcome. `code` and `status` are the ONLY fields safe to hand a logger: the
    // error keeps the SDK original on `cause`, and Sentry's linkedErrors
    // integration walks cause chains into telemetry.
    options.logError?.('signInWithPasskey', { code: mapped.code, status: mapped.status });
    toast({
      title: t(SIGN_IN_FAILED_TITLE),
      description: t('passkey.genericError'),
      variant: 'destructive',
    });
  };

  /**
   * A ceremony the cap already walked away from can still settle minutes later,
   * and the adapter measures that as a REAL session: the SDK does not re-check
   * the signal before posting the assertion. Nothing is mounted to discharge it
   * by then, so it takes the same compensating revoke `result.aborted` does.
   *
   * Writes no state on purpose — the hook moved on when the cap won, and this
   * continuation outlives the render that started it. It touches neither the
   * step-up obligation nor the session ref for the same reason: by now both may
   * belong to a newer attempt.
   */
  const compensateLateCeremony = async (ceremony: Promise<PasskeySignInResult>): Promise<void> => {
    try {
      await ceremony;
    } catch (error) {
      // A rejected ceremony minted no session, so there is nothing to compensate
      // — and the user was already told the sign-in failed when the cap won. It
      // is still the only measurement of what a capped browser eventually does,
      // so it is classified and logged rather than swallowed. `code` and `status`
      // only: the error keeps the SDK original on `cause`, which Sentry's
      // linkedErrors integration would walk into telemetry.
      const mapped = mapPasskeyError(error);
      options.logError?.('passkeyLateCeremony', { code: mapped.code, status: mapped.status });
      return;
    }

    // Resolving replaced the stored session with THIS ceremony's aal1 one, so
    // that is what gets revoked — and only while it is still the stored one. If
    // anything has replaced it since, it is not this flow's to touch.
    await revokeMintedSession('passkeyLateCeremonySignOut', await captureMintedSession());
  };

  /** The cap won, so this owns every consequence of walking away from a live promise. */
  const reportCeremonyTimeout = (
    controller: AbortController,
    ceremony: Promise<PasskeySignInResult>,
    epoch: number,
  ): void => {
    // Best effort, deliberately not awaited or depended on: a browser that
    // honours the signal closes its sheet and rejects, and the shell that
    // motivated the cap ignores it entirely.
    controller.abort();
    void compensateLateCeremony(ceremony);

    if (!isCurrent(epoch)) {
      return;
    }
    // The one signal that separates "this browser cannot finish a ceremony" from
    // the ordinary failures above, so it is logged rather than swallowed. A
    // duration and nothing else — no error, no credential, no PII.
    options.logError?.('passkeyCeremonyTimeout', { timeoutMs: CEREMONY_TIMEOUT_MS });
    toast({
      title: t(SIGN_IN_FAILED_TITLE),
      description: t('passkey.timeoutDescription'),
      variant: 'destructive',
    });
  };

  const startPasskeySignIn = async (): Promise<void> => {
    // Single-flight. A second ceremony supersedes the first inside the adapter
    // and the loser resolves as `cancelled`, which reads to the user as "my
    // passkey failed".
    if (ceremonyRef.current !== null) {
      return;
    }
    const epoch = currentEpoch();
    const controller = new AbortController();
    ceremonyRef.current = controller;
    // Owned by THIS attempt. A shared handle would let a stale attempt's
    // `finally` cancel the cap a newer attempt installed, leaving the successor
    // uncapped — the latch back, one attempt later.
    const deadline = startCeremonyDeadline();
    deadlineRef.current = deadline;
    setNotice('none');
    setIsLoading(true);

    try {
      // Held in a variable rather than awaited inline: the cap may win the race,
      // and the losing promise is still the only handle on a session it can mint
      // afterwards. INSIDE the try because everything that releases the latch
      // lives in the `finally` — a synchronous throw from here would skip it and
      // leave `ceremonyRef` set and `isLoading` true, which is the latch this
      // whole race exists to break. Currently unreachable (the adapter is
      // `async`), and deliberately not resting on that.
      const ceremony = runPasskeyCeremony(controller.signal);

      const outcome = await deadline.race(ceremony);

      if (outcome === CEREMONY_TIMED_OUT) {
        reportCeremonyTimeout(controller, ceremony, epoch);
        return;
      }

      // The SDK persists the session and emits `SIGNED_IN` BEFORE the sign-in
      // call returns, so by here this reads the session this ceremony minted —
      // which is what every teardown below is aimed at, and what stops one of
      // them landing on a session some other sign-in method owns.
      const minted = await captureMintedSession();

      // IDENTITY, not the unmount flag. This ref is only still `controller` when
      // nothing has unmounted, capped, or superseded this exact attempt.
      if (ceremonyRef.current !== controller || outcome.aborted) {
        await revokeMintedSession(ABANDON_LABEL, minted);
        return;
      }

      mintedSessionRef.current = minted;
      // The credential worked, so the hint is earned regardless of what the
      // step-up does next (R11).
      setPasskeyHint();
      setSignedInUserId(outcome.userId);
      // The discoverable flow collects no email; `MFALoginView` renders without
      // the "signing in as" line when it is absent.
      setUserEmail(outcome.email ?? '');
      await resolveStepUp(outcome.userId, minted, epoch);
    } catch (error) {
      if (!isCurrent(epoch)) {
        return;
      }
      reportCeremonyFailure(error);
    } finally {
      // Only ever release what this attempt took. Ordering makes a stale release
      // unreachable today — the cap frees both before a second press can set
      // them — but "unreachable given the current ordering" is what the whole
      // class of bug above was made of.
      deadline.cancel();
      if (deadlineRef.current === deadline) {
        deadlineRef.current = null;
      }
      if (ceremonyRef.current === controller) {
        ceremonyRef.current = null;
      }
      if (isCurrent(epoch)) {
        // One reset covering what the password hook spends two calls on: the
        // trigger must re-enable whether `onSignedIn` navigated away (a no-op on
        // an unmounted tree), returned after surfacing a destructive toast, the
        // ceremony failed, or the cap released it. Without it the button spins
        // forever behind the toast.
        setIsLoading(false);
      }
    }
  };

  const retryStepUpCheck = async (): Promise<void> => {
    if (signedInUserId === null) {
      return;
    }
    const epoch = currentEpoch();
    setNotice('none');
    // Handed to the check: `resolveStepUp` re-arms the obligation if a step-up
    // is still owed, and compensates itself if the page unmounts mid-flight.
    stepUpOwedRef.current = false;
    setIsLoading(true);
    try {
      await resolveStepUp(signedInUserId, mintedSessionRef.current, epoch);
    } finally {
      if (isCurrent(epoch)) {
        setIsLoading(false);
      }
    }
  };

  const releaseStepUp = (): void => {
    // Drops the unmount compensation for the same reason this does not revoke:
    // the claiming flow is mid-sign-in, and the storage slot is changing hands.
    stepUpOwedRef.current = false;
    mintedSessionRef.current = null;
    setNotice('none');
    setSignedInUserId(null);
  };

  const verifyMFA = async (code: string): Promise<void> => {
    // A late click from a step-up view that has already been dismissed: the
    // factor is gone, and verifying without one reports a bogus failure.
    if (mfaFactorId === null) {
      return;
    }
    const epoch = currentEpoch();
    const minted = mintedSessionRef.current;
    // This call owns the obligation from here: an unmount mid-verify must not
    // compensate, because the branches below decide by outcome (a verified code
    // makes the session aal2 and complete, a rejected one leaves it aal1).
    stepUpOwedRef.current = false;
    setIsLoading(true);

    try {
      const result = await withRequestDeadline(verifyMFACode(mfaFactorId, code), VERIFY_TIMED_OUT);
      if (!isCurrent(epoch)) {
        if (!result.success) {
          // A rejected code leaves the aal1 session from the ceremony live with
          // no view left to re-prompt in. A verified one is aal2 and complete —
          // revoking THAT would tear down a session the route guard admits.
          void revokeMintedSession(ABANDON_LABEL, minted);
        }
        return;
      }

      if (!result.success) {
        // Hand the obligation back. The step-up view stays up over the same aal1
        // session, so an unmount from HERE (Back, a link, a redirect) is still an
        // exit with a step-up owed and must compensate — the mirror of the
        // stale branch above, which already revokes on a rejected code.
        stepUpOwedRef.current = true;
        toast({
          title: t('passkey.verificationFailedTitle'),
          description: t(MFA_VERIFY_ERROR_KEYS[classifyMfaVerifyError(result.error)]),
          variant: 'destructive',
        });
        return;
      }

      // Stepped up: the session is aal2 and complete, and the token this flow
      // could have revoked no longer exists.
      mintedSessionRef.current = null;
      if (signedInUserId !== null) {
        await options.onSignedIn(signedInUserId);
      }
    } finally {
      if (isCurrent(epoch)) {
        setIsLoading(false);
      }
    }
  };

  const signOutAndDismiss = async (): Promise<void> => {
    const epoch = currentEpoch();
    const minted = mintedSessionRef.current;
    // This exit compensates, so the unmount path must not fire a second, racing
    // revoke when the user navigates away while this one is in flight.
    stepUpOwedRef.current = false;
    mintedSessionRef.current = null;
    setIsLoading(true);

    // AWAITED, and mirroring `mfa-verify.tsx`'s cancel rather than the password
    // hook's `dismissMFA` (which leaves a live aal1 session behind — a linked
    // follow-up, deliberately untouched here). The form must not come back while
    // the session the user just walked away from is still valid.
    //
    // Deliberate on failure: the form still comes back. Stranding the user on a
    // view they asked to leave, with no way out, is worse than a session that
    // outlives the dismiss — and the route guard's step-up check is the
    // remaining challenger for it. The failure is logged, never swallowed.
    await revokeMintedSession('passkeyDismissSignOut', minted);

    if (!isCurrent(epoch)) {
      return;
    }
    setShowMFAInput(false);
    setMfaFactorId(null);
    setUserEmail('');
    setSignedInUserId(null);
    setNotice('none');
    setIsLoading(false);
  };

  /**
   * Synchronous by contract — `MFALoginView`'s `onBack` and the password hook's
   * `dismissMFA` are both `() => void`, and the page selects one controller for
   * either. Asynchronous inside: the revoke completes before the form is allowed
   * back, so the view never returns over a live aal1 session.
   */
  const dismissMFA = (): void => {
    void signOutAndDismiss();
  };

  const abandonSignIn = (): void => {
    // The same two steps, in the same order, that the unmount cleanup runs -
    // and for the same reason. A host that dismisses its sign-in UI without
    // unmounting never reaches that cleanup, so it calls this instead.
    ceremonyRef.current?.abort();
    if (stepUpOwedRef.current) {
      void signOutAndDismiss();
    }
  };

  /**
   * Drops the advisory notice and nothing else.
   *
   * Deliberately NOT folded into `abandonSignIn`: that one owns the session
   * teardown and its two steps are load-bearing in that order. A host that
   * reopens a dismissed sign-in surface wants a clean slate visually without
   * asking for anything to happen to a session.
   */
  const clearNotice = (): void => {
    setNotice('none');
  };

  return {
    isLoading,
    showMFAInput,
    userEmail,
    notice,
    startPasskeySignIn,
    verifyMFA,
    dismissMFA,
    abandonSignIn,
    clearNotice,
    retryStepUpCheck,
    releaseStepUp,
  };
}
